import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../api/axiosConfig';
import {
  Container,
  Form,
  Button,
  Alert,
  Spinner,
  Row,
  Col,
  Card
} from 'react-bootstrap';
import './EditWebsite.css';

const EditWebsite = () => {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    brand_name: '',
    http_link: '',
    logo_path: '',
    vision: '',
    mission: '',
    about_us: '',
    email: '',
    phone_number: '',
    address: ''
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchWebsite = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/sites/${websiteId}`);
        const websiteData = response.data;
        setFormData({
          brand_name: websiteData.brand_name || '',
          http_link: websiteData.http_link || '',
          logo_path: websiteData.logo_path || '',
          vision: websiteData.vision || '',
          mission: websiteData.mission || '',
          about_us: websiteData.about_us || '',
          email: websiteData.email || '',
          phone_number: websiteData.phone_number || '',
          address: websiteData.address || ''
        });
      } catch (err) {
        setError('An error occurred while fetching website data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchWebsite();
  }, [websiteId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await api.put(`/sites/${websiteId}`, formData);

      if (response.data.success) {
        setSuccessMessage('Website updated successfully!');
        setTimeout(() => {
          navigate('/super-admin/websites');
        }, 1500);
      } else {
        setError(response.data.message || 'Failed to update website.');
      }
    } catch (err) {
      setError('An error occurred while updating the website.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this website? This action cannot be undone.")) {
      setSubmitting(true);
      setError('');
      setSuccessMessage('');

      try {
        await api.delete(`/sites/${websiteId}`);
        setSuccessMessage('Website deleted successfully!');
        setTimeout(() => {
          navigate('/super-admin/websites');
        }, 1500);
      } catch (err) {
        setError('An error occurred while deleting the website.');
        console.error(err);
      } finally {
        setSubmitting(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="edit-website-container">
        <div className="loading-container">
          <Spinner animation="border" className="loading-spinner" />
          <p className="loading-text">Loading website data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-website-container">
      {/* Header */}
      <div className="edit-website-header">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className="edit-website-title">Edit Website</h1>
            <p className="websites-subtitle">Update website information and settings</p>
          </div>
          <Button
            variant="outline-secondary"
            className="btn-cancel"
            onClick={() => navigate('/super-admin/websites')}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Websites
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" className="alert-custom alert-danger-custom">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert variant="success" className="alert-custom alert-success-custom">
          <i className="fas fa-check-circle me-2"></i>
          {successMessage}
        </Alert>
      )}

      {/* Form */}
      <Card className="edit-website-form-card">
        <Form onSubmit={handleSubmit}>
          {/* Basic Information Section */}
          <div className="form-section">
            <h3 className="form-section-title">
              <i className="fas fa-info-circle"></i>
              Basic Information
            </h3>
            <Row>
              <Col md={6}>
                <Form.Group className="form-group-custom">
                  <Form.Label className="form-label-custom required">
                    Brand Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="brand_name"
                    value={formData.brand_name}
                    onChange={handleInputChange}
                    className="form-control-custom"
                    placeholder="Enter brand name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="form-group-custom">
                  <Form.Label className="form-label-custom required">
                    Website URL
                  </Form.Label>
                  <Form.Control
                    type="url"
                    name="http_link"
                    value={formData.http_link}
                    onChange={handleInputChange}
                    className="form-control-custom"
                    placeholder="https://example.com"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="form-group-custom">
              <Form.Label className="form-label-custom">
                Logo Path
              </Form.Label>
              <Form.Control
                type="text"
                name="logo_path"
                value={formData.logo_path}
                onChange={handleInputChange}
                className="form-control-custom"
                placeholder="/path/to/logo.png"
              />
              <Form.Text className="form-help-text">
                Path to the website logo file
              </Form.Text>
            </Form.Group>
          </div>

          {/* Vision & Mission Section */}
          <div className="form-section">
            <h3 className="form-section-title">
              <i className="fas fa-bullseye"></i>
              Vision & Mission
            </h3>
            <Form.Group className="form-group-custom">
              <Form.Label className="form-label-custom">
                Vision Statement
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="vision"
                value={formData.vision}
                onChange={handleInputChange}
                className="form-control-custom"
                placeholder="Enter the company's vision statement"
              />
            </Form.Group>
            <Form.Group className="form-group-custom">
              <Form.Label className="form-label-custom">
                Mission Statement
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="mission"
                value={formData.mission}
                onChange={handleInputChange}
                className="form-control-custom"
                placeholder="Enter the company's mission statement"
              />
            </Form.Group>
          </div>

          {/* About Us Section */}
          <div className="form-section">
            <h3 className="form-section-title">
              <i className="fas fa-building"></i>
              About Us
            </h3>
            <Form.Group className="form-group-custom">
              <Form.Label className="form-label-custom">
                About Us Description
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="about_us"
                value={formData.about_us}
                onChange={handleInputChange}
                className="form-control-custom"
                placeholder="Tell us about your company..."
              />
            </Form.Group>
          </div>

          {/* Contact Information Section */}
          <div className="form-section">
            <h3 className="form-section-title">
              <i className="fas fa-address-book"></i>
              Contact Information
            </h3>
            <Row>
              <Col md={6}>
                <Form.Group className="form-group-custom">
                  <Form.Label className="form-label-custom">
                    Email Address
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-control-custom"
                    placeholder="contact@example.com"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="form-group-custom">
                  <Form.Label className="form-label-custom">
                    Phone Number
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    className="form-control-custom"
                    placeholder="+1 (555) 123-4567"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="form-group-custom">
              <Form.Label className="form-label-custom">
                Address
              </Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className="form-control-custom"
                placeholder="Enter full address"
              />
            </Form.Group>
          </div>

          {/* Action Buttons */}
          <div className="form-buttons">
            <Button
              type="submit"
              className="btn-update"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Updating...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  Update Website
                </>
              )}
            </Button>
            
            <Button
              variant="outline-secondary"
              className="btn-cancel"
              onClick={() => navigate('/super-admin/websites')}
              disabled={submitting}
            >
              <i className="fas fa-times me-2"></i>
              Cancel
            </Button>
            
            <Button
              variant="danger"
              className="btn-delete"
              onClick={handleDelete}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <i className="fas fa-trash me-2"></i>
                  Delete Website
                </>
              )}
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default EditWebsite;